import {useState, useEffect} from "react";

//
export function useLocalState(key, initialState)
{
	const localState = localStorage.getItem(key);
	if(localState) {
		try {
			initialState = JSON.parse(localState);
		} catch {}
	}

	const [state, setState] = useState(initialState);

	const onSetState = (newState) => {
		setState(state => {
			if(typeof newState === "function") {
				newState = newState(state);
			}

			localStorage.setItem(key, JSON.stringify(newState));

			return newState;
		});
	};


	// track local changes
	useEffect(() => {
		const handler = function (e) {
			if(e.key === key) {
				setState(JSON.parse(e.newValue));
			}
		}

		window.addEventListener("storage", handler);
		return () => window.removeEventListener('storage', handler);
	}, [key]);

	return [state, onSetState]
}

//
const idbInstances = {};

export function useIndexedDB(dbName, storeName)
{
	const [db, setDB] = useState(null);
	const [items, setItems] = useState([]);
	const [updates, setUpdates] = useState(0);
	const [filters, setFilters] = useState({});

	// connection
	useEffect(() => {
		if (idbInstances[dbName]){
			setDB(idbInstances[dbName]);
			return;
		}

		const request = indexedDB.open(dbName, 1);
		request.onsuccess = () => {
			idbInstances[dbName] = request.result;
			setDB(idbInstances[dbName]);
		};

		request.onerror = () => {
			console.log('Error opening IndexedDB database');
		};

		request.onupgradeneeded = (event) => {
			// todo: make it dynamic
			const db = event.target.result;
			const store = db.createObjectStore("products", { keyPath: 'id' });
			store.createIndex("categories_ids", "categories_ids", {multiEntry: true});
		};
	}, [dbName]);

	// get items
	useEffect(() => {
		if (db) {
			const transaction = db.transaction(storeName, 'readonly');
			const store = transaction.objectStore(storeName);
			const items = [];

			let oc;
			if(filters.index?.value) {
				oc = store.index(filters.index.name).openCursor(IDBKeyRange.only(filters.index.value));
			} else {
				oc = store.openCursor();
			}

			oc.onsuccess = (event) => {
				const cursor = event.target.result;
				if (cursor && items.length < 50) {
					if(!filters.filter || filters.filter(cursor.value)) {
						items.push(cursor.value);
					}

					cursor.continue();
				} else {
					setItems(items);
				}
			};
		}
	}, [db, storeName, updates, filters]);

	// add items
	const addItems = (items) => {
		const transaction = db.transaction(storeName, 'readwrite');
		const store = transaction.objectStore(storeName);

		if(!Array.isArray(items)) {
			items = [items];
		}

		items.forEach(function(item) {
			store.put(item)
		});

		setUpdates(updates + 1)

		return transaction.complete;
	};

	//
	return {
		dbReady: !!db,
		applyFilters: setFilters,
		items,
		addItems
	};
}

//
export function useOnScreen(ref, callback)
{
	useEffect(() => {
		if(!ref.current || !callback) {
			return;
		}

		const observer = new IntersectionObserver(([entry]) => {
			if(entry.isIntersecting) {
				callback();
				observer.disconnect();
			}
		});

		if (ref.current) {
			observer.observe(ref.current);
		}

		return () => {
			observer.disconnect();
		};
	}, [ref]);
}
